import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ToolId } from 'config/toolDefinitionsConfig';
import type { Segmentation, SegmentationControllerType, SegmentationProps } from 'config/analysisConfig';
import {
  clearConnect2DSelection,
  enterOrExitManualSegmentation,
  getVolumeMinMax,
  hasManualSegmentationSourceVolume,
  setModifierEvents,
  setModifierOverlay,
} from '../../rendering/Panels/Renderers/CornerstoneManager';
import {
  clearSegmentManager,
  discardSegmentManager,
  exitSegmentManager,
  ModifierType,
  setStateApplier,
} from '../../segmentManager';
import { scheduleRender } from '../../rendering/Panels/renderState';
import { handleEditModeAction } from '@features/view/Modification';
import { useConfig } from 'config/useConfig';


export function useManualSegmentationController({
  segmentation,
  selectedKey,
  selectedSeriesId,
  selectedSeriesPixelSpacing,
  contextPoolSurface3D,
  selectedPanelComponentKey,
  lowQuality,
  manualMode,
  dirty,
  modifierType,
  handleToolActivate,
  onDisableRegulatorConnect,
  setUndoable,
  setRedoable,
  handleSetSelectedKey,
  setMode,
  setDirty
}: {
  segmentation: Segmentation;
  selectedKey: SegmentationControllerType;
  selectedSeriesId?: string | null;
  selectedSeriesPixelSpacing?: number | null;
  contextPoolSurface3D: unknown;
  selectedPanelComponentKey?: string;
  lowQuality: boolean;
  manualMode: boolean;
  dirty: boolean;
  modifierType: ModifierType;
  handleToolActivate: (toolId: ToolId) => void;
  onDisableRegulatorConnect: () => void;
  setUndoable: (value: boolean) => void;
  setRedoable: (value: boolean) => void;
  handleSetSelectedKey: (key: SegmentationControllerType) => void;
  setMode: (value: boolean) => void;
  setDirty: (value: boolean) => void;
}) {
  const pixelSpacing = (lowQuality ? 2 : 1) * (selectedSeriesPixelSpacing ?? 1);
  const brushUnit = pixelSpacing * 0.5 + 1e-3;
  const [brushStep, setBrushStep] = useState<number>(5);
  const [startValue, setStartValue] = useState<number>(80);
  const [endValue, setEndValue] = useState<number>(500);
  const [manualSourceVolumeRetryTick, setManualSourceVolumeRetryTick] = useState(0);
  const [HUMin, HUMax] = useMemo(
    () => getVolumeMinMax(selectedSeriesId!, lowQuality),
    [lowQuality, selectedSeriesId],
  );

  const selectedKeyRef = useRef<SegmentationControllerType>(selectedKey);
  const handleSetSelectedKeyRef = useRef(handleSetSelectedKey);
  const { setData } = useConfig();
  //const handleManualExitRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  useEffect(() => {
    handleSetSelectedKeyRef.current = handleSetSelectedKey;
  }, [handleSetSelectedKey]);

  const handleRequestModifierStyle = useCallback(
    (params: {
      colorHex?: string;
      radiusMM?: number;
      brushStep?: number;
      brushUnit?: number;
      brushStepDelta?: number;
      range?: [number, number];
      modifierType?: ModifierType;
    }) => {
      scheduleRender('Axial', { type: 'uknowuknow', ...params });
      scheduleRender('Coronal', { type: 'uknowuknow', ...params });
      scheduleRender('Sagittal', { type: 'uknowuknow', ...params });
    },
    [],
  );

  const handleBrushThresholdChange = useCallback(
    (start: number, end: number) => {
      if (start === startValue && end === endValue) return;
      if (start > end) return;
      if (start !== startValue) setStartValue(start);
      if (end !== endValue) setEndValue(end);
      handleRequestModifierStyle({ range: [start, end] });
    },
    [endValue, handleRequestModifierStyle, startValue],
  );

  const handleBrushStepChange = useCallback(
    (newBrushStep: number) => {
      if (newBrushStep === brushStep) return;
      setBrushStep(newBrushStep);
      handleRequestModifierStyle({
        radiusMM: newBrushStep * brushUnit,
        brushStep: newBrushStep,
        brushUnit,
      });
    },
    [brushStep, brushUnit, handleRequestModifierStyle],
  );

  const EDIT_MANUAL_BRUSH_SIZE_DELTA_EVENT =
    'analysis:edit-manual-brush-size-delta';

  useEffect(() => {
    const handleBrushSizeDelta = (event: Event) => {
      if (
        !manualMode ||
        (modifierType !== ModifierType.BRUSH &&
          modifierType !== ModifierType.ERASER &&
        modifierType !== ModifierType.CONNECT_2D)
      )
        return;
      const delta = Number(
        (event as CustomEvent<{ delta?: number }>).detail?.delta,
      );
      if (!Number.isFinite(delta) || delta === 0) return;
      const nextBrushStep = Math.max(
        1,
        Math.min(10, Math.round(brushStep + delta)),
      );
      handleBrushStepChange(nextBrushStep);
    };

    window.addEventListener(
      EDIT_MANUAL_BRUSH_SIZE_DELTA_EVENT,
      handleBrushSizeDelta,
    );
    return () => {
      window.removeEventListener(
        EDIT_MANUAL_BRUSH_SIZE_DELTA_EVENT,
        handleBrushSizeDelta,
      );
    };
  }, [brushStep, handleBrushStepChange, manualMode, modifierType]);



  const handleManualEnter = useCallback(
    (toolId: ToolId) => {
      onDisableRegulatorConnect();
      handleToolActivate(toolId);
      setMode(true);
    },
    [handleToolActivate, onDisableRegulatorConnect],
  );

  const handleManualExit = useCallback(() => {
    if (selectedPanelComponentKey === 'Surface3D') {
      handleToolActivate('Rotation');
    } else {
      handleToolActivate('Scroll');
    }
    clearConnect2DSelection();
    clearSegmentManager();
    setMode(false);
    setData((prev) => ({
      viewportSwitchPendingMap: {
        Surface3D: true,
        Axial: true,
        Coronal: true,
        Sagittal: true,
      },
    }));
  }, [handleToolActivate, selectedPanelComponentKey]);

  // useEffect(() => {
  //   handleManualExitRef.current = handleManualExit;
  // }, [handleManualExit]);

  const handleManualToolEnter = useCallback(
    (toolId: ToolId) => {
      handleManualEnter(toolId);
      if(toolId !== 'Brush'){
        clearConnect2DSelection();
      }
      if (selectedPanelComponentKey) {
        handleEditModeAction('Eraser', selectedPanelComponentKey);
      }
    },
    [handleManualEnter, selectedPanelComponentKey],
  );

  useEffect(() => {
    if (!segmentation) return;
    if (!selectedSeriesId) return;
    if (!manualMode) return;
    if (!hasManualSegmentationSourceVolume(selectedSeriesId, lowQuality)) {
    const retryTimer = window.setTimeout(() => {
      setManualSourceVolumeRetryTick((prev) => prev + 1);
    }, 120);

    return () => {
      window.clearTimeout(retryTimer);
    };
  }

    enterOrExitManualSegmentation(
      selectedSeriesId,
      contextPoolSurface3D,
      true,
      lowQuality,
      segmentation,
    );

    const entrySelectedKey = selectedKeyRef.current;
    if (entrySelectedKey && segmentation[entrySelectedKey]) {
      handleSetSelectedKeyRef.current(entrySelectedKey);
    }

    setStateApplier({
      undoableSetter: setUndoable,
      redoableSetter: setRedoable,
      activeLabelSetter: (key: SegmentationControllerType) => handleSetSelectedKeyRef.current(key),
    });

    return () => {
      discardSegmentManager(setDirty);
      enterOrExitManualSegmentation(
        selectedSeriesId,
        contextPoolSurface3D,
        false,
        lowQuality,
        segmentation,
      );
      exitSegmentManager();
      //handleManualExitRef.current();
    };
  }, [
    contextPoolSurface3D,
    lowQuality,
    manualMode,
    manualSourceVolumeRetryTick,
    segmentation,
    selectedSeriesId,
    setRedoable,
    setUndoable,
  ]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!manualMode) return;
    if (modifierType === ModifierType.IDLE) return;

    const radiusMM = brushStep * brushUnit;
    const range: [number, number] = [startValue, endValue];
    const removers = [
      ...setModifierOverlay({
        radiusMM,
        brushStep,
        brushUnit,
        range,
        modifierType,
      }),
      ...setModifierEvents({
        segKey: selectedKey,
        seriesId: selectedSeriesId,
        modifierType,
        lowQuality,
        range,
      }),
    ];
    return () => {
      removers.forEach((func) => {
        if (func) func();
      });
    };
  }, [
    brushStep,
    brushUnit,
    endValue,
    lowQuality,
    manualMode,
    modifierType,
    selectedKey,
    selectedSeriesId,
    startValue,
  ]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!manualMode) return;
    if (modifierType === ModifierType.IDLE) return;

    handleRequestModifierStyle({
      modifierType,
      radiusMM: brushStep * brushUnit,
      brushStep,
      brushUnit,
      range: [startValue, endValue],
    });
  }, [
    brushStep,
    brushUnit,
    endValue,
    handleRequestModifierStyle,
    manualMode,
    modifierType,
    selectedSeriesId,
    startValue,
  ]);

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (!manualMode || !dirty) return;
    return () => {
      // keep the previous cleanup timing intentionally unchanged.
    };
  }, [dirty, manualMode, selectedSeriesId]);

  return {
    brushStep,
    startValue,
    endValue,
    HUMin,
    HUMax,
    brushUnit,
    handleRequestModifierStyle,
    handleBrushThresholdChange,
    handleBrushStepChange,
    handleManualEnter,
    handleManualExit,
    handleManualToolEnter,
  };
}
